import { calcSelected } from '../../../../app/helpers/calc-selected';
import { omit } from '../../../../core/util/utils';
import { Lookup } from '../../../business-objects/lookup/lookup.model';
import { TextControl } from '../text-control.model';
import { SearchResultModel } from './search-result.model';
import * as actions from './text-control.actions';
import { getInputValueFromLookup, getMatches } from './text-control.helpers';

export interface InstanceState {
    // the instance of control
    control: TextControl;
    // the id of the highlighted result
    highlightedResultId: string;
    // the current value of the input
    inputValue: string;
    // because text-controls can appear more than once on the page we keep the
    // state for each instance of text-control under the instances object. Each
    // instance has a key which is a unique string for every instance of the
    // text-control on the page. When the text-control is destroyed we remove
    // the instance state object
    instanceKey: string;
    // the number of results to show
    limit: number;
    // the id of the lookup type this instance depends on
    lookupType: string;
    // the list of results to display
    results: Array<SearchResultModel>;
    // the number of results on each page
    pageSize: number;
    // flag if we should show all results
    showAllResults: boolean;
    // the string that look ups will be matched against
    searchTerm: string;
    // flag if we should show the results box
    showResults: boolean;
}

export interface State {
    instances: {[key: string]: InstanceState};
    lookups: {[key: string]: Array<Lookup>};
}

export const initialState: State = {
    instances: {},
    lookups: {}
};

const initialInstanceState: InstanceState = {
    control: undefined,
    highlightedResultId: '',
    inputValue: '',
    instanceKey: '',
    limit: 100,
    lookupType: undefined,
    pageSize: 100,
    results: [],
    searchTerm: '',
    showAllResults: false,
    showResults: false
};

// tslint:disable-next-line:only-arrow-functions
export function reducer(
    state: State = initialState,
    action: actions.Actions
): State {
    try {
        switch (action.type) {
            case actions.DESTROY: {

                const instanceKey = action.payload.instanceKey;
                const instance = state.instances[instanceKey];
                const lookupType = instance ? instance.lookupType : undefined;

                if (!lookupType) {
                    return {
                        ...state,
                        instances: omit(
                            state.instances,
                            action.payload.instanceKey
                        )
                    };
                }

                // we calculate if there are no other instances that depend on
                // the lookups by counting how many instances, other than the
                // one we are removing, have the same lookup type
                const removeLookupTypes = Object.keys(state.instances)
                    .map(key => state.instances[key])
                    .reduce((p, c) => {
                        return c.instanceKey !== instanceKey &&
                        c.lookupType === lookupType ?
                            p + 1 :
                            p;
                    }, 0) === 0;

                let lookups = state.lookups;

                if (removeLookupTypes && lookupType) {
                    lookups = omit(state.lookups, lookupType);
                }

                return {
                    ...state,
                    instances: omit(
                        state.instances,
                        action.payload.instanceKey
                    ),
                    lookups
                };
            }
            case actions.INIT_SUCCESS: {
                return {
                    ...state,
                    lookups: {
                        ...state.lookups,
                        [action.payload.control.config.lookupType]:
                            [...action.payload.lookups]
                                .sort((a, b) => a.title.localeCompare(b.title))
                    },
                    instances: {
                        ...state.instances,
                        [action.payload.instanceKey]: {
                            ...initialInstanceState,
                            control: action.payload.control,
                            inputValue: action.payload.inputValue || '',
                            instanceKey: action.payload.instanceKey,
                            lookupType: action.payload.control.config.lookupType
                        }
                    }
                };
            }
            case actions.RELOAD_LOOKUPS_SUCCESS: {
                return {
                    ...state,
                    lookups: {
                        ...state.lookups,
                        [action.payload.lookupTypeId]: action.payload.lookups
                    }
                };
            }
            default: {

                if (action.type.startsWith('[Text Control]')) {

                    const instance =
                        state.instances[action.payload.instanceKey];

                    if (!instance) {
                        return state;
                    }

                    return {
                        ...state,
                        instances: {
                            ...state.instances,
                            [action.payload.instanceKey]: instanceReducer(
                                instance,
                                action,
                                state.lookups[instance.lookupType]
                            )
                        }
                    };
                }

                return state;
            }
        }
    } catch (e) {
        console.error(e);
        return state;
    }
}

// tslint:disable-next-line:only-arrow-functions
export function instanceReducer(
    state: InstanceState = initialInstanceState,
    action: actions.Actions,
    allLookups: Array<Lookup>
): InstanceState {

    switch (action.type) {
        case actions.BLUR: {
            return {
                ...state,
                limit: initialInstanceState.limit,
                results: initialInstanceState.results,
                searchTerm: initialInstanceState.searchTerm,
                showAllResults: initialInstanceState.showAllResults,
                showResults: initialInstanceState.showResults
            };
        }
        case actions.CLICK_RESULT: {
            return {
                ...state,
                inputValue: getInputValueFromLookup(
                    state.results.find(
                        result =>
                            result.id === action.payload.result.id
                    ),
                    state.control.config.resultValue
                ),
                limit: initialInstanceState.limit,
                searchTerm: initialInstanceState.searchTerm,
                showAllResults: initialInstanceState.showAllResults,
                showResults: initialInstanceState.showResults
            };
        }
        case actions.KEY_ENTER: {
            return {
                ...state,
                inputValue: getInputValueFromLookup(
                    state.results.find(
                        result =>
                            result.id === state.highlightedResultId
                    ),
                    state.control.config.resultValue
                ),
                limit: initialInstanceState.limit,
                results: initialInstanceState.results,
                searchTerm: initialInstanceState.searchTerm,
                showAllResults: initialInstanceState.showAllResults,
                showResults: initialInstanceState.showResults
            };
        }
        case actions.KEY_ESC: {
            return {
                ...state,
                limit: initialInstanceState.limit,
                results: initialInstanceState.results,
                searchTerm: initialInstanceState.searchTerm,
                showAllResults: initialInstanceState.showAllResults,
                showResults: initialInstanceState.showResults
            };
        }
        case actions.KEY_DOWN: {

            const showAllResults = state.showAllResults || !state.showResults;

            const matches = getMatches(
                !showAllResults ? state.searchTerm : '',
                allLookups,
                state.limit
            );

            const highlightedResultId = calcSelected(
                // if showAllResults is changing from false to true,
                // we pass in empty string to reset the selected item to the
                // first result
                !state.showAllResults && showAllResults ?
                    '' :
                    state.highlightedResultId,
                1,
                matches
            );

            const results = matches
                .map(lookup => ({
                    id: lookup.id,
                    highlighted: lookup.id === highlightedResultId,
                    text: lookup.title,
                    content: lookup.content
                } as SearchResultModel));

            return {
                ...state,
                highlightedResultId,
                results,
                showResults: true,
                showAllResults
            };
        }
        case actions.KEY_UP: {

            const showAllResults = state.showAllResults || !state.showResults;

            const matches = getMatches(
                !showAllResults ? state.searchTerm : '',
                allLookups,
                state.limit
            );

            const highlightedResultId = calcSelected(
                // if showAllResults is changing from false to true,
                // we pass in empty string to reset the selected item to the
                // first result
                !state.showAllResults && showAllResults ?
                    '' :
                    state.highlightedResultId,
                -1,
                matches
            );

            const results = matches
                .map(lookup => ({
                    id: lookup.id,
                    highlighted: lookup.id === highlightedResultId,
                    text: lookup.title,
                    content: lookup.content
                } as SearchResultModel));

            return {
                ...state,
                highlightedResultId,
                results,
                showResults: true,
                showAllResults
            };
        }
        case actions.LOAD_MORE: {

            const limit = state.limit + state.pageSize;

            const matches = getMatches(
                state.searchTerm,
                allLookups,
                limit
            );

            const results = matches
                .map(lookup => ({
                    id: lookup.id,
                    highlighted: lookup.id === state.highlightedResultId,
                    text: lookup.title,
                    content: lookup.content
                } as SearchResultModel));

            return {
                ...state,
                limit,
                results
            };
        }
        case actions.SET_SEARCH_TERM: {

            const matches = getMatches(
                action.payload.searchTerm,
                allLookups,
                state.limit
            );

            const results = matches
                .map(lookup => ({
                    id: lookup.id,
                    highlighted: false,
                    text: lookup.title,
                    content: lookup.content
                } as SearchResultModel));

            return {
                ...state,
                results,
                searchTerm: action.payload.searchTerm,
                showResults: action.payload.searchTerm.length > 0,
                showAllResults: false
            };
        }
        case actions.SET_SHOW : {
            return {
                ...state,
                showResults: action.payload.showResults
            };
        }
        case actions.SHOW_ALL_DESKTOP_SUCCESS: {

            const matches = getMatches(
                '',
                allLookups,
                state.limit
            );

            const results = matches
                .map(lookup => ({
                    id: lookup.id,
                    highlighted: lookup.id === matches[0].id,
                    text: lookup.title,
                    content: lookup.content
                } as SearchResultModel));

            return {
                ...state,
                results,
                showAllResults: true,
                highlightedResultId: matches.length ? matches[0].id : undefined
            };
        }
        case actions.SHOW_ALL_MOBILE_SUCCESS: {

            if (!action.payload.lookupId) {
                return state;
            }

            return {
                ...state,
                inputValue: getInputValueFromLookup(
                    state.results.find(
                        lookup => lookup.id ===
                            action.payload.lookupId
                    ),
                    state.control.config.resultValue
                ),
                searchTerm: '',
                showAllResults: false,
                showResults: false
            };
        }
        default: {
            return state;
        }
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/text/form/text-control.reducer.ts